import Dexie from 'dexie';

const DB_NAME = 'FigshelfWidgetsDB';
const DB_VERSION = 1;

const stableStringify = (value) => {
  if (!value) return '';
  try {
    if (typeof value !== 'object') return String(value);
    const keys = Object.keys(value).sort();
    const obj = {};
    keys.forEach((k) => {
      obj[k] = value[k];
    });
    return JSON.stringify(obj);
  } catch {
    return '';
  }
};

export const makeWidgetKey = ({ scope, userId, params }) => {
  const uid = userId || 'anon';
  const p = stableStringify(params);
  return `${scope || 'widget'}:${uid}:${p}`;
};

class WidgetsCacheDb extends Dexie {
  constructor() {
    super(DB_NAME);

    this.version(DB_VERSION).stores({
      widgets: 'key, updatedAt',
    });

    this.widgets = this.table('widgets');
  }
}

let dbInstance = null;

const isDatabaseClosedError = (error) => {
  if (!error) return false;
  if (error?.name === 'DatabaseClosedError') return true;
  if (typeof error?.message === 'string' && error.message.toLowerCase().includes('database has been closed')) {
    return true;
  }
  if (typeof error?.message === 'string' && error.message.toLowerCase().includes('db.open() was cancelled')) {
    return true;
  }
  return false;
};

const resetWidgetsCacheDb = () => {
  // Do not call db.close() here.
  // Dexie can throw/reject during close/open cancellation races (e.g. after indexedDB.deleteDatabase()),
  // which can surface as unhandled rejections. Dropping the singleton reference is enough; a fresh
  // instance will be created on the next read/write.
  dbInstance = null;
};

export const getWidgetsCacheDb = () => {
  if (dbInstance && typeof dbInstance.isOpen === 'function' && !dbInstance.isOpen()) {
    resetWidgetsCacheDb();
  }

  if (!dbInstance) {
    dbInstance = new WidgetsCacheDb();
  }
  return dbInstance;
};

export const getCachedWidget = async (key) => {
  if (!key) return null;
  try {
    const db = getWidgetsCacheDb();
    const row = await db.widgets.get(key);
    return row?.data || null;
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetWidgetsCacheDb();
      const db = getWidgetsCacheDb();
      const row = await db.widgets.get(key);
      return row?.data || null;
    }
    throw error;
  }
};

export const cacheWidget = async (key, payload) => {
  if (!key) return;
  try {
    const db = getWidgetsCacheDb();
    await db.widgets.put({
      key,
      updatedAt: new Date().toISOString(),
      data: payload,
    });
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetWidgetsCacheDb();
      const db = getWidgetsCacheDb();
      await db.widgets.put({
        key,
        updatedAt: new Date().toISOString(),
        data: payload,
      });
      return;
    }
    throw error;
  }
};
